import React, { useState, useEffect } from 'react';
import { getFounderData, FounderData } from '../services/founderService';

const AboutPage: React.FC = () => {
  const [founderData, setFounderData] = useState<FounderData>({
    name: 'Deborah Lawson',
    picture: '/images/loom.jpeg',
    title: 'Founder, CEO & Creative Director',
    bio: `Deborah Lawson is a visionary textile designer and entrepreneur whose passion for sustainable luxury has revolutionized the textile industry. Born in Barcelona to a family of traditional weavers, Deborah's journey began at the age of 12 when she first learned to operate her grandmother's antique loom.

After graduating with honors from the prestigious Central Saint Martins in London, Deborah spent a decade working with some of the world's most renowned fashion houses, including Chanel, Hermès, and Valentino. Her innovative approach to sustainable materials and traditional craftsmanship earned her recognition as one of the industry's most promising young designers.

In 2003, at the age of 28, Deborah founded Vogueloom with a bold vision: to create textiles that would not only meet the highest standards of luxury and quality but would also set new benchmarks for environmental responsibility. Her groundbreaking work with organic fibers, natural dyes, and zero-waste production methods has earned Vogueloom numerous industry awards and certifications.

Under Deborah's leadership, Vogueloom has grown from a small workshop to an internationally recognized brand, collaborating with luxury hotels, prestigious interior design firms, and sustainable fashion labels. Her commitment to preserving traditional weaving techniques while embracing cutting-edge technology has made Vogueloom a beacon of innovation in the textile world.

Deborah is also a passionate advocate for women in business and sustainable design. She serves on the board of several environmental organizations and regularly speaks at international conferences about the future of sustainable luxury textiles. Her TED Talk on "The Art of Sustainable Luxury" has been viewed by millions worldwide.

When not in her studio or traveling to source the finest materials, Deborah can be found mentoring young designers, teaching master classes in traditional weaving, or exploring ancient textile traditions in remote villages around the world. Her personal collection of antique looms and textiles is considered one of the most comprehensive in Europe.`
  });

  useEffect(() => {
    // Load founder data from Firebase (with localStorage fallback)
    const loadData = async () => {
      try {
        const data = await getFounderData();
        if (data) {
          setFounderData(data);
        }
      } catch (error) {
        console.error('Error loading founder data:', error);
        // Fallback to localStorage if Firebase fails
        const savedData = localStorage.getItem('founder_data');
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            setFounderData(parsed);
          } catch (e) {
            console.error('Error parsing localStorage data:', e);
          }
        }
      }
    };
    loadData();
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-midnight via-deep-purple to-royal-purple">
          <div className="absolute inset-0 bg-[url('/images/Caversham Textiles and Designs – Modern South African Designs/imgi_112_PF-Wallpaper-1.jpg')] bg-cover bg-center bg-no-repeat opacity-20"></div>
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center space-x-2 mb-8">
              <span className="text-lavender/60">Home</span>
              <span className="text-lavender/40">/</span>
              <span className="text-lavender">About</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold text-white mb-8 animate-fade-in drop-shadow-2xl">
              Our Story
            </h1>
            
            {/* Subtitle */}
            <p className="text-xl md:text-2xl lg:text-3xl text-lavender mb-10 font-medium animate-slide-up drop-shadow-xl max-w-3xl mx-auto">
              Two decades of passion, innovation, and textile excellence
            </p>
            
            {/* Description */}
            <p className="text-lg md:text-xl text-white/90 mb-12 max-w-4xl mx-auto leading-relaxed animate-slide-up drop-shadow-lg">
              Founded by visionary artisans who believed that textiles could tell stories, 
              Vogueloom has been weaving dreams into reality for over 20 years. 
              Our journey is marked by an unwavering commitment to craftsmanship, 
              sustainability, and the belief that every thread matters.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-2">20+</div>
                <div className="text-lavender/80">Years of Excellence</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-2">500+</div>
                <div className="text-lavender/80">Collections Created</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-2">50+</div>
                <div className="text-lavender/80">Awards Won</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-8 left-8 hidden lg:block">
          <div className="w-32 h-32 border-2 border-lavender/30 rotate-45 rounded-3xl animate-float"></div>
        </div>
        <div className="absolute top-20 right-12 hidden lg:block">
          <div className="w-20 h-20 bg-gradient-to-br from-warm-gold/20 to-sunset-orange/20 rounded-full border border-warm-gold/30 animate-pulse-slow"></div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gradient-primary">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Story */}
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-8">
                The Vogueloom Journey
              </h2>
              <div className="space-y-6 text-lg text-sage leading-relaxed">
                <p>
                  In the early 2000s, a group of passionate textile designers came together with a shared vision: 
                  to create fabrics that would not only serve their functional purpose but also tell stories, 
                  evoke emotions, and stand the test of time.
                </p>
                <div className="my-6">
                  <img 
                    src="/images/Plain-Mini-Matt-Fabrics/imgi_221_MiniMatt_Colour07_Twist-Edit.jpg" 
                    alt="Traditional textile workshop" 
                    className="w-full h-48 object-cover rounded-xl shadow-lg"
                  />
                </div>
                <p>
                  What started as a small workshop in a converted textile mill has grown into one of the most 
                  respected names in premium textiles. Our founders' commitment to traditional craftsmanship 
                  combined with cutting-edge innovation has been the cornerstone of our success.
                </p>
                <p>
                  Today, Vogueloom continues to push boundaries, creating textiles that blend timeless elegance 
                  with modern sustainability, ensuring that every piece we create carries forward the legacy 
                  of our founders while embracing the future of textile design.
                </p>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img
                  src="/images/Print Fabrics – Lively Fabrics/imgi_28_9168bb4e-1d43-4a2c-8fb8-cc7c516f0caa.jpg"
                  alt="Vogueloom workshop"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight/40 via-transparent to-transparent"></div>
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-8 -left-8 bg-white/95 backdrop-blur-sm border border-lavender/30 rounded-2xl p-6 shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-warm-gold to-sunset-orange rounded-xl flex items-center justify-center">
                    <span className="text-white text-xl">🏆</span>
                  </div>
                  <div>
                    <div className="font-semibold text-charcoal">Award-Winning</div>
                    <div className="text-sm text-sage">Design Excellence</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-gradient-secondary">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gradient-gold mb-6">
              Our Philosophy
            </h2>
            <p className="text-xl text-sage max-w-3xl mx-auto">
              Three core principles that guide every decision we make
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card p-8 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-royal-purple/20 to-amethyst/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🎨</span>
              </div>
              <h3 className="text-xl font-serif font-semibold text-charcoal mb-4">Artistic Excellence</h3>
              <p className="text-sage">
                Every fabric is a canvas where creativity meets precision, ensuring that beauty and functionality 
                exist in perfect harmony.
              </p>
              <div className="mt-6">
                <img 
                  src="/images/Buy Clothing Fabrics by Category, Online Shop - SK Textiles/imgi_18_CHIFFON-WOVEN_LILLY-PRINT_WPPR1506-17101-450x450.jpg" 
                  alt="Artistic textile design" 
                  className="w-full h-32 object-cover rounded-xl"
                />
              </div>
            </div>

            <div className="glass-card p-8 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-warm-gold/20 to-sunset-orange/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🌱</span>
              </div>
              <h3 className="text-xl font-serif font-semibold text-charcoal mb-4">Sustainable Innovation</h3>
              <p className="text-sage">
                We believe in creating textiles that respect our planet, using eco-friendly processes and 
                materials that ensure a better future.
              </p>
              <div className="mt-6">
                <img 
                  src="/images/The Stain Guard Range™️_ Protection🍷 – Ragland Fabrics/imgi_259_MarsalaTS.png" 
                  alt="Sustainable textile process" 
                  className="w-full h-32 object-cover rounded-xl"
                />
              </div>
            </div>

            <div className="glass-card p-8 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-ocean-blue/20 to-teal-wave/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🤝</span>
              </div>
              <h3 className="text-xl font-serif font-semibold text-charcoal mb-4">Human-Centered Design</h3>
              <p className="text-sage">
                Every textile we create is designed with people in mind, ensuring comfort, durability, 
                and joy in everyday use.
              </p>
              <div className="mt-6">
                <img 
                  src="/images/Online Clothing Fabrics Shop - Vega Textiles/imgi_149_IMG_1320.jpg" 
                  alt="Human-centered textile design" 
                  className="w-full h-32 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CEO/Founder Section */}
      <section className="py-20 bg-gradient-accent">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-6">
              Meet Our Founder
            </h2>
            <p className="text-xl text-sage max-w-3xl mx-auto">
              The visionary leader behind Vogueloom's success story
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Founder Image */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img
                  src={founderData.picture}
                  alt={`${founderData.name}, ${founderData.title} of Vogueloom`}
                  className="w-full h-96 object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/loom.jpeg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight/40 via-transparent to-transparent"></div>
              </div>
              
              {/* Floating Achievement Card */}
              <div className="absolute -bottom-8 -right-8 bg-white/95 backdrop-blur-sm border border-lavender/30 rounded-2xl p-6 shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-warm-gold to-sunset-orange rounded-xl flex items-center justify-center">
                    <span className="text-white text-xl">👑</span>
                  </div>
                  <div>
                    <div className="font-semibold text-charcoal">Industry Leader</div>
                    <div className="text-sm text-sage">20+ Years Experience</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Founder Bio */}
            <div className="space-y-6">
              <div className="mb-8">
                <h3 className="text-3xl md:text-4xl font-serif font-bold text-gradient mb-3">
                  {founderData.name}
                </h3>
                <p className="text-xl text-royal-purple font-semibold mb-4">
                  {founderData.title}
                </p>
                <div className="w-20 h-1 bg-gradient-to-r from-warm-gold to-sunset-orange rounded-full"></div>
              </div>

              <div className="space-y-4 text-lg text-sage leading-relaxed">
                {founderData.bio.split('\n\n').map((paragraph, index) => (
                  paragraph.trim() && (
                    <p key={index}>
                      {paragraph.trim()}
                    </p>
                  )
                ))}
              </div>

              {/* Key Achievements */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                <div className="bg-white/80 backdrop-blur-sm border border-lavender/30 rounded-xl p-4">
                  <div className="text-2xl font-serif font-bold text-gradient mb-1">25+</div>
                  <div className="text-sage text-sm">Industry Awards</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm border border-lavender/30 rounded-xl p-4">
                  <div className="text-2xl font-serif font-bold text-gradient mb-1">15+</div>
                  <div className="text-sage text-sm">Patents Filed</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm border border-lavender/30 rounded-xl p-4">
                  <div className="text-2xl font-serif font-bold text-gradient mb-1">50+</div>
                  <div className="text-sage text-sm">Countries Served</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm border border-lavender/30 rounded-xl p-4">
                  <div className="text-2xl font-serif font-bold text-gradient mb-1">100%</div>
                  <div className="text-sage text-sm">Sustainable</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
